import journeyRoutes, { JOURNEY_ROUTE_NAMES } from '@/modules/journeys/routes';
import loginRoutes, { LOGIN_ROUTE_NAMES } from '@/modules/login/routes';
import maintenanceRoutes from '@/modules/maintenance/routes';
import { createRouter, createWebHistory, NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import globalValues from './helpers/enums/globalValues';

// Route name constants to avoid magic strings
const ROUTE_NAMES = {
   ERROR: 'Error',
   JOURNEY: JOURNEY_ROUTE_NAMES.JOURNEY,
   JOURNEY_QUESTION: JOURNEY_ROUTE_NAMES.JOURNEY_QUESTION,
   JOURNEY_DECLINE: JOURNEY_ROUTE_NAMES.JOURNEY_DECLINE,
   REGISTER: LOGIN_ROUTE_NAMES.REGISTER,
   SUBMISSION: JOURNEY_ROUTE_NAMES.SUBMISSION,
   MAINTENANCE: 'maintenance-index',
} as const;

// Public routes that don't require authentication
const PUBLIC_ROUTES = new Set([ROUTE_NAMES.REGISTER, ROUTE_NAMES.JOURNEY, ROUTE_NAMES.SUBMISSION, ROUTE_NAMES.JOURNEY_DECLINE]);

/**
 * Check if candidate session exists
 */
const hasCandidateSession = (to: any): boolean => {
   const hasId = sessionStorage.getItem(globalValues.candidate.id) !== null;
   const hasJourneyRandom = sessionStorage.getItem(globalValues.candidate.journeyRandom) !== null;
   const isPreviewMode = !!to.query?.preview;

   return hasId || hasJourneyRandom || isPreviewMode;
};

/**
 * Check if route is publicly accessible
 */
const isPublicRoute = (routeName: string | null | undefined): boolean => {
   return routeName ? PUBLIC_ROUTES.has(routeName as any) : false;
};

/**
 * Build registration redirect URL
 */
const buildRegistrationUrl = (to: RouteLocationNormalized): { path: string; query: typeof to.query } => {
   const { journeyId, referral, submissionId } = to.params;
   const submissionPart = submissionId ? `/${submissionId}` : '';
   return {
      path: `/journey/${journeyId}/${referral}/register${submissionPart}`,
      query: to.query,
   };
};

// Define routes with proper typing
const routes: RouteRecordRaw[] = [
   {
      path: '/error',
      name: ROUTE_NAMES.ERROR,
      component: () => import(/* webpackChunkName: "error" */ '@/modules/error/pages/Index.vue'),
   },
   ...journeyRoutes,
   ...loginRoutes,
   ...maintenanceRoutes,
   {
      path: '/:pathMatch(.*)*',
      redirect: { name: ROUTE_NAMES.ERROR },
   },
];

const router = createRouter({
   history: createWebHistory(import.meta.env.BASE_URL),
   routes,
});

// Get maintenance mode from environment or default to false
const IS_IN_MAINTENANCE = import.meta.env.VITE_APP_MAINTENANCE_MODE === 'true';

/**
 * Navigation guard for authentication and maintenance mode
 */
router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
   // Handle maintenance mode
   if (IS_IN_MAINTENANCE) {
      if (to.name === ROUTE_NAMES.MAINTENANCE) {
         next();
         return;
      }
      next({ name: ROUTE_NAMES.MAINTENANCE, query: { redirect: decodeURI(to.fullPath) } });
      return;
   }

   // Handle maintenance mode exit redirect
   if (to.name === ROUTE_NAMES.MAINTENANCE) {
      const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : '/';
      next(redirect || '/');
      return;
   }

   // Allow public routes
   if (isPublicRoute(to.name)) {
      next();
      return;
   }

   // Check authentication for protected routes
   if (!hasCandidateSession(to)) {
      sessionStorage.setItem(globalValues.redirect, to.fullPath);
      next(buildRegistrationUrl(to));
      return;
   }

   // All checks passed, proceed
   next();
});

export default router;

